import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import Footer from '../../../components/common/Footer';
import Header from '../../../components/user/common/Header';
import SpinnerLoader from '../../../components/common/SpinnerLoader';
import { Helmet } from 'react-helmet';
import { useSelector, useDispatch } from "react-redux";
import { addToCart } from "../../../store/reducers/cart-slice-reducer";
import {
    setLoading,
    setIsLoginModalOpen,
} from "../../../store/reducers/global-reducer";
import arrow_back from '../../../assets/icons/arrow-back.svg';


const ListedProducts = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");

    const [listedProducts, setListedProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortOption, setSortOption] = useState("new_to_old");
    const [isLoading, setIsLoading] = useState(false);
    const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
    const [Discount, setDiscount] = useState('');
    const [DiscountId, setDiscountId] = useState('');

    const itemsPerPage = 4;

    const sortingOptions = [
        { value: "new_to_old", label: "New to Old" },
        { value: "old_to_new", label: "Old to New" },
    ];

    const authInfo = useSelector((state) => state.auth.authInfo);
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

    /*****************************************************************************/
    /*****************************************************************************/
    useEffect(() => {
        window.scrollTo(0, 0);
        fetchCategories(); // Fetch categories on component mount
    }, []);

    useEffect(() => {
        if (id) fetchDealProducts();
    }, [id]);

    useEffect(() => {
        applyFilters();
    }, [listedProducts, currentPage, sortOption, selectedCategory]);

    /*****************************************************************************/

    const fetchCategories = async () => {
        setIsCategoriesLoading(true);
        try {
            const response = await axios.get('front/categories'); 
            
            console.log('Categories API Response:', response.data); // Debug log
            
            if (response.data.status) {
                const categoriesData = response.data.data;
                
                console.log('Categories fetched:', categoriesData); // Debug log
                
                setCategories(categoriesData);
            } else {
                console.error('Failed to fetch categories:', response.data.message);
                toast.error('Failed to fetch categories');
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Error fetching categories');
        } finally {
            setIsCategoriesLoading(false);
        }
    };

    const fetchDealProducts = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`front/product/today-dealsById/${id}`);
            if (response.data.status) {
                const products = response.data.data.productId || [];
                setDiscount(response.data.data.discount)
                setDiscountId(response.data.data._id)
                setListedProducts(products);
                
                setTotalPages(Math.ceil(products.length / itemsPerPage));
                setCurrentPage(1);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error("Error fetching data.");
        } finally {
            setIsLoading(false);
        }
    };

    /*****************************************************************************/

    const applyFilters = () => {
        let filteredByCategory = [...listedProducts];

        // Apply category filter
        if (selectedCategory) {
            filteredByCategory = filteredByCategory.filter(product => 
                product.category === selectedCategory
            );
        }

        // Apply sorting
        let sortedProducts = [...filteredByCategory];
        if (sortOption === "new_to_old") {
            sortedProducts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } else if (sortOption === "old_to_new") {
            sortedProducts.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        }

        // Update total pages based on filtered results
        const totalFilteredPages = Math.ceil(sortedProducts.length / itemsPerPage);
        setTotalPages(totalFilteredPages);

        // Reset to page 1 if current page exceeds total filtered pages
        const validCurrentPage = currentPage > totalFilteredPages ? 1 : currentPage;
        if (validCurrentPage !== currentPage) {
            setCurrentPage(validCurrentPage);
        }

        // Apply pagination
        const startIndex = (validCurrentPage - 1) * itemsPerPage;
        const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);
        setFilteredProducts(paginatedProducts);
    };

    /*****************************************************************************/

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    /*****************************************************************************/

    const handleSortChange = (selectedOption) => {
        setSortOption(selectedOption.value);
        setCurrentPage(1);
    };

    /*****************************************************************************/

    const handleCategoryChange = (selectedOption) => {
        setSelectedCategory(selectedOption ? selectedOption.value : "");
        setCurrentPage(1);
    };

    /*****************************************************************************/

    const clearFilters = () => {
        setSelectedCategory("");
        setSortOption("new_to_old");
        setCurrentPage(1);
    };

    /*****************************************************************************/

    const openmodalHandler = () => {
        toast.error("Buyer login failed....");
        setTimeout(() => {
            dispatch(setIsLoginModalOpen({ isLoginModalOpen: true }));
            document.body.style.overflow = "hidden";
        }, 2000);
    };

    /*****************************************************************************/

    // Helper function to get category name by ID
    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => (cat._id || cat.id) === categoryId);
        return category ? category.categoryName : 'Unknown Category';
    };

    /*****************************************************************************/

    // Generate pagination numbers
    const renderPaginationNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`btn btn-sm mx-1 ${currentPage === i ? 'btn-primary' : 'btn-outline-primary'}`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    /*****************************************************************************/

    // Create category options for Select component
    const categoryOptions = [
        { value: "", label: "All Categories" },
        ...categories.map(category => ({ 
            value: category._id || category.id, 
            label: category.categoryName 
        }))
    ];

    console.log("filteredProducts", filteredProducts);
    console.log("categories", categories);
    
    return (
        <React.Fragment>
            {isLoading === true ? <SpinnerLoader /> : ''}
            <div className="seller_body">
                <Header />
                <div className="inr_top_page_title">
                    <h2>Deal of the day</h2>
                </div>
                <Helmet>
                    <title>{"Deal of the day - Pay Earth"}</title>
                </Helmet>
                <div className="seller_dash_wrap pt-2 pb-5">
                    <div className="container ">
                        <div className="bg-white rounded-3 pt-3 pb-5">
                            <div className="dash_inner_wrap">
                                <nav className="seller_tabs">
                                    <div className="nav nav-tabs d-flex justify-content-between align-items-center" id="nav-tab" role="tablist">
                                        <div className="d-flex align-items-center gap-3">
                                            {/* Category Filter */}
                                            <div className="filter-group">
                                                <label className="form-label mb-1 text-sm">Category:</label>
                                                <Select
                                                    className="category_select text-normal"
                                                    options={categoryOptions}
                                                    value={categoryOptions.find((option) => option.value === selectedCategory)}
                                                    onChange={handleCategoryChange}
                                                    placeholder="All Categories"
                                                    isClearable
                                                    isLoading={isCategoriesLoading}
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minWidth: '200px',
                                                            fontSize: '14px'
                                                        })
                                                    }}
                                                />
                                            </div>
                                            
                                            {/* Clear Filters Button */}
                                            {(selectedCategory || sortOption !== "new_to_old") && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={clearFilters}
                                                >
                                                    Clear Filters
                                                </button>
                                            )}
                                        </div>

                                        <div className="d-flex align-items-center gap-3">
                                            {/* Sort Options */}
                                            <div className="filter-group">
                                                <label className="form-label mb-1 text-sm">Sort by:</label>
                                                <Select
                                                    className="sort_select text-normal"
                                                    options={sortingOptions}
                                                    value={sortingOptions.find((option) => option.value === sortOption)}
                                                    onChange={handleSortChange}
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minWidth: '140px',
                                                            fontSize: '14px'
                                                        })
                                                    }}
                                                />
                                            </div>

                                            {/* Back Button */}
                                            <button
                                                type="button"
                                                className="btn custom_btn btn_yellow rounded-pill px-4 py-1"
                                                onClick={() => window.history.back()}
                                                style={{ height: "40px" }}
                                            >
                                                <img src={arrow_back} alt="back" />&nbsp;
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </nav>
                                
                                {/* Results Summary */}
                                {listedProducts.length > 0 && (
                                    <div className="results-summary mt-3 mb-2">
                                        <p className="text-muted mb-0">
                                            Showing {filteredProducts.length} of {selectedCategory ? 
                                                listedProducts.filter(p => p.category === selectedCategory).length : 
                                                listedProducts.length} products
                                            {selectedCategory && ` in "${getCategoryName(selectedCategory)}"`}
                                        </p>
                                    </div>
                                )}

                                <div className="tab-content" id="nav-tabContent">
                                    <div className="tab-pane fade show active" id="nav-listed-product" role="tabpanel" aria-labelledby="nav-listed-product-tab">
                                        {filteredProducts.length > 0 ? (
                                            <>
                                                <div className="row">
                                                    {filteredProducts.map((product, index) => (
                                                        <div className="col-md-3 mb-4" key={index}>
                                                            <div className="card h-100">
                                                                <Link
                                                                    to={`/product-detail/${product.id}`}
                                                                >
                                                                    <img
                                                                        src={product.featuredImage}
                                                                        loading="lazy"
                                                                        decoding="async"
                                                                        className="card-img-top"
                                                                        alt="..."
                                                                        style={{ height: '200px', objectFit: 'cover' }}
                                                                    />
                                                                </Link>
                                                                <div className="card-body d-flex flex-column">
                                                                    <h5 className="card-title">{product.name}</h5>
                                                                    
                                                                    {/* Category Badge */}
                                                                    {product.category && (
                                                                        <span className="badge bg-secondary mb-2 align-self-start">
                                                                            {getCategoryName(product.category)}
                                                                        </span>
                                                                    )}
                                                                    
                                                                    {product.price && (
                                                                        <p className="card-text">
                                                                            <span style={{ textDecoration: 'line-through', marginRight: '8px', color: '#888' }}>
                                                                                ${product.price.toFixed(2)}
                                                                            </span>
                                                                            <span style={{ fontWeight: 'bold', color: 'green' }}>
                                                                                ${(product.price - (product.price * Discount) / 100).toFixed(2)} USD
                                                                            </span>
                                                                        </p>
                                                                    )}

                                                                    <div className="mt-auto">
                                                                        {product.quantity && product.quantity.stock_qty !== 0 ? (
                                                                            <div className="d-flex prod_foot gap-2">
                                                                                {isLoggedIn ? (
                                                                                    <>
                                                                                        <Link
                                                                                            className="btn custom_btn btn_yellow_bordered flex-fill"
                                                                                            to={'/my-cart'}
                                                                                            onClick={() =>
                                                                                                dispatch(
                                                                                                    addToCart({
                                                                                                        id: product.id,
                                                                                                        name: product.name,
                                                                                                        image: product.featuredImage,
                                                                                                        price: product.price,
                                                                                                        discountId: DiscountId,
                                                                                                        discountPercent: Discount
                                                                                                    })
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Buy Now
                                                                                        </Link>

                                                                                        <Link
                                                                                            className="btn custom_btn btn_yellow flex-fill"
                                                                                            to={`#`}
                                                                                            onClick={() =>
                                                                                                dispatch(
                                                                                                    addToCart({
                                                                                                        id: product.id,
                                                                                                        name: product.name,
                                                                                                        image: product.featuredImage,
                                                                                                        price: product.price,
                                                                                                        quantity: 1,
                                                                                                        discountId: DiscountId,
                                                                                                        discountPercent: Discount
                                                                                                    })
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Add to cart
                                                                                        </Link>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Link
                                                                                            className="btn custom_btn btn_yellow flex-fill"
                                                                                            to="#"
                                                                                            onClick={openmodalHandler}
                                                                                        >
                                                                                            Buy Now
                                                                                        </Link>
                                                                                        <Link
                                                                                            className="btn custom_btn btn_yellow_bordered flex-fill"
                                                                                            to="#"
                                                                                            onClick={() =>
                                                                                                dispatch(
                                                                                                    addToCart({
                                                                                                        id: product.id,
                                                                                                        name: product.name,
                                                                                                        image: product.featuredImage,
                                                                                                        price: product.price,
                                                                                                        discountId: DiscountId,
                                                                                                        discountPercent: Discount
                                                                                                    })
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            Add to cart
                                                                                        </Link>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <h5 className="text-danger text-center">Out of stock</h5>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                
                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <nav aria-label="Products pagination" className="mt-4">
                                                        <div className="d-flex justify-content-center align-items-center">
                                                            <button
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                disabled={currentPage === 1}
                                                                className="btn btn-outline-primary btn-sm me-2"
                                                            >
                                                                Previous
                                                            </button>
                                                            
                                                            {renderPaginationNumbers()}
                                                            
                                                            <button
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                disabled={currentPage === totalPages}
                                                                className="btn btn-outline-primary btn-sm ms-2"
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                        <div className="text-center mt-2">
                                                            <small className="text-muted">
                                                                Page {currentPage} of {totalPages}
                                                            </small>
                                                        </div>
                                                    </nav>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-center py-5">
                                                <h5>No products found</h5>
                                                <p className="text-muted">
                                                    {selectedCategory ? 
                                                        `No products available in "${getCategoryName(selectedCategory)}" category.` : 
                                                        "No products available at the moment."}
                                                </p>
                                                {selectedCategory && (
                                                    <button 
                                                        className="btn btn-primary btn-sm" 
                                                        onClick={() => setSelectedCategory("")}
                                                    >
                                                        View All Products
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </React.Fragment>
    );
};

export default ListedProducts;